import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { itensDeMenu } from './menu-itens';
// ⚠️ O menu passou a depender das capacidades em 18/09/2026: é delas que sai o
//    item do Chamados (e, conforme os módulos migrarem, o dos demais).
import { useCapacidades } from '../utils/capacidades';

// Ícone "Recolher Menu" do Figma: quadrado arredondado com a divisória à DIREITA
// e um chevron apontando para a direita (`[>|]`). É a mesma arte do Figma, com as
// coordenadas já giradas 90°. Não há equivalente direto no lucide, então vai inline.
// `currentColor` segue a cor do item (branco / hover).
const IconeRecolher = ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 3V21" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 9L11 12L8 15" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// `mostrarBotaoMobile`: por padrão a Sidebar mostra o botão flutuante (hambúrguer)
// no canto superior esquerdo no mobile. A Home passa `false` porque já tem o
// hambúrguer na própria barra superior mobile (evita botão duplicado).
const Sidebar = ({ isCollapsed, setIsCollapsed, mostrarBotaoMobile = true }) => {
    const [hoveredItem, setHoveredItem] = useState(null);
    const [user, setUser] = useState(null);
    // Novo estado para detectar se é tela de celular
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Listener para atualizar o estado isMobile se a janela for redimensionada
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        
        // No mobile, o padrão deve ser iniciar colapsado (escondido)
        if (window.innerWidth <= 768) {
            setIsCollapsed(true);
        }
        
        return () => window.removeEventListener('resize', handleResize);
    }, [setIsCollapsed]);

    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                setUser(JSON.parse(userStr));
            } catch (e) {
                console.error("Erro ao ler user do localStorage", e);
            }
        }
    }, []);

    const bgSidebar = '#990605';
    const textNormal = '#ffe4e6';
    const textActive = '#ffffff';   // texto/ícone do item selecionado (branco sobre o vermelho da seleção)
    const bgActive = '#BD0D0D';     // cor da seleção do módulo (antes era branco)
    const bgHover = '#be1212';

    // ⚠️ `caps` já vem preenchido quando a resposta do servidor foi buscada uma
    //    vez nesta página (ver o snapshot em `utils/capacidades.js`) — por isso o
    //    menu não pisca ao trocar de rota.
    const { caps } = useCapacidades();
    const itensMenu = itensDeMenu(user, caps);

    // Se estiver no mobile e o menu estiver aberto, tratamos como expandido para mostrar os textos
    const isActuallyCollapsed = isMobile ? false : isCollapsed;

    return (
        <>
            {/* Overlay escuro para Mobile - Fecha o menu ao clicar fora */}
            {isMobile && !isCollapsed && (
                <div 
                    onClick={() => setIsCollapsed(true)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 998,
                        transition: 'opacity 0.3s'
                    }}
                />
            )}

            {/* Botão Mobile */}
            <button 
                onClick={() => setIsCollapsed(!isCollapsed)}
                style={{ 
                    position: 'fixed', 
                    top: '15px', 
                    left: '15px', 
                    zIndex: 1000, 
                    background: '#fff', 
                    border: '1px solid #e2e8f0', 
                    padding: '8px',
                    borderRadius: '8px',
                    display: (isMobile && mostrarBotaoMobile) ? 'block' : 'none',
                    color: bgSidebar,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                {/* Se o menu estiver aberto no mobile, mostra um X para fechar, senão mostra o Menu */}
                {isMobile && !isCollapsed ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Container principal do Sidebar */}
            <div style={{
                // Largura: 260px expandido (mobile inclusive, quando visível) e 100px recolhido.
                // ⚠️ Os 260px são DUPLICADOS no marginLeft de cada tela (getMarginLeft) —
                // mudar aqui sem mudar lá deixa o conteúdo por baixo do menu ou com faixa branca.
                width: (!isMobile && isCollapsed) ? '100px' : '260px',
                // A mágica acontece aqui: no mobile, se estiver colapsado, jogamos 100% para a esquerda (esconde)
                transform: (isMobile && isCollapsed) ? 'translateX(-100%)' : 'translateX(0)',
                height: '100vh',
                backgroundColor: bgSidebar,
                position: 'fixed',
                left: 0,
                top: 0,
                transition: 'transform 0.3s ease, width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '4px 0 15px rgba(225, 29, 72, 0.2)',
                zIndex: 999
            }}>
                {/* LOGO DA EMPRESA — logo completo (arco + nome + tagline) no expandido;
                    no recolhido cabe só o arco branco (logotipo) nos 80px. */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isActuallyCollapsed ? '22px 0' : '24px 16px' }}>
                    <img
                        src={isActuallyCollapsed ? '/logotipo.png' : '/logotipo-grupo-correia.png'}
                        alt="Logo Grupo Correia"
                        style={{ width: isActuallyCollapsed ? '60px' : '100%', maxWidth: isActuallyCollapsed ? '60px' : '200px', height: 'auto', objectFit: 'contain', transition: 'all 0.3s' }}
                    />
                </div>

                {/* LINKS DE NAVEGAÇÃO — alinhados à DIREITA (rótulo à esquerda, ícone à
                    direita), com a barra ativa/hover ocupando a largura toda (full-bleed),
                    como no Figma. Sem padding horizontal no <nav> p/ a barra encostar nas
                    bordas; o respiro lateral fica no padding de cada item. */}
                <nav className="sidebar-nav" style={{ flex: 1, padding: '16px 0', display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto' }}>
                    {itensMenu.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => isMobile && setIsCollapsed(true)} // Fecha o menu no mobile ao clicar num link
                            onMouseEnter={() => setHoveredItem(item.path)}
                            onMouseLeave={() => setHoveredItem(null)}
                            style={({ isActive }) => {
                                const isHovered = hoveredItem === item.path;
                                return {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: isActuallyCollapsed ? '12px' : '12px 20px',
                                    textDecoration: 'none',
                                    fontWeight: isActive ? '700' : '500',
                                    fontSize: '15px',
                                    transition: 'all 0.2s ease',
                                    justifyContent: isActuallyCollapsed ? 'center' : 'flex-end',
                                    backgroundColor: isActive ? bgActive : (isHovered ? bgHover : 'transparent'),
                                    color: isActive ? textActive : (isHovered ? '#fff' : textNormal),
                                    boxShadow: isActive ? '0 4px 6px rgba(0,0,0,0.1)' : 'none'
                                };
                            }}
                        >
                            {!isActuallyCollapsed && <span>{item.label}</span>}
                            {item.icon}
                        </NavLink>
                    ))}
                </nav>

                {/* RODAPÉ DO MENU — Recolher / Expandir o menu (único controle de colapso).
                    O "Sair" saiu daqui e foi para a barra superior da Home (no lugar da engrenagem). */}
                <div className="account-section" style={{ padding: '16px 0' }}>
                    <div
                        className="sidebar-item"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        onMouseEnter={() => setHoveredItem('toggle')}
                        onMouseLeave={() => setHoveredItem(null)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: isActuallyCollapsed ? '12px' : '12px 20px',
                            cursor: 'pointer', transition: 'all 0.2s ease',
                            backgroundColor: hoveredItem === 'toggle' ? bgHover : 'transparent',
                            color: hoveredItem === 'toggle' ? '#fff' : textNormal,
                            justifyContent: isActuallyCollapsed ? 'center' : 'flex-end'
                        }}
                    >
                        {!isActuallyCollapsed && <span style={{ fontSize: '15px', fontWeight: '700' }}>Recolher Menu</span>}
                        <IconeRecolher size={20} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;